import React from 'react';
import { Button, Dialog, Flex, Notice, Select, Text, Textarea, createForm, useId, useToasts } from '@applyboard/crystal-ui';
import { useApplication, useCreateSelfServeTuitionDepositRefundRequest } from "../../data";
import { CheckCircleOutlineIcon } from '@applyboard/ui-icons';
import { useEventTracker } from '@applyboard/ui-utils';
import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import { jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { Fragment as _Fragment } from "@emotion/react/jsx-runtime";
var _createForm = createForm(),
  Form = _createForm.Form,
  Field = _createForm.Field,
  useFieldValues = _createForm.useFieldValues;
export var RequestTuitionDepositRefundDialog = function RequestTuitionDepositRefundDialog(props) {
  var refundFormId = useId();
  var _useApplication = useApplication(),
    application = _useApplication.application;
  var _useCreateSelfServeTu = useCreateSelfServeTuitionDepositRefundRequest(),
    createSelfServeRequest = _useCreateSelfServeTu.createSelfServeRequest,
    isCreatingSelfServeRequest = _useCreateSelfServeTu.isCreatingSelfServeRequest,
    createdSelfServeRequest = _useCreateSelfServeTu.createdSelfServeRequest,
    resetCreateSelfServeRequest = _useCreateSelfServeTu.resetCreateSelfServeRequest;
  var toasts = useToasts();
  var _useEventTracker = useEventTracker(),
    trackEvent = _useEventTracker.trackEvent;
  return _jsxs(Dialog, {
    open: props.isOpen,
    onOpenChange: function onOpenChange(isOpen) {
      if (!isOpen) {
        trackEvent({
          event: 'Single Application - Action Menu - Request a Tuition Refund - Closed Modal'
        });
      }
      resetCreateSelfServeRequest();
      props.onOpenChange(isOpen);
    },
    size: "md",
    children: [_jsx(Dialog.Heading, {
      children: createdSelfServeRequest ? 'Tuition Refund Request Successfully Sent' : 'Request Tuition Refund'
    }), _jsx(Dialog.Content, {
      children: createdSelfServeRequest ? _jsxs(Flex, {
        gap: 4,
        children: [_jsx(CheckCircleOutlineIcon, {
          size: "xl",
          intent: "positive"
        }), _jsxs(Flex, {
          direction: "column",
          gap: 4,
          children: [_jsx(Text, {
            variant: "bodyM",
            intent: "secondary",
            children: "Your request has been successfully submitted to ApplyBoard. It has been recorded on the Student Records tab."
          }), _jsx(Text, {
            variant: "bodyM",
            intent: "secondary",
            children: "Please allow our team 48-72 business hours to review your request. We ask that you do not submit any duplicate requests."
          })]
        })]
      }) : _jsx(Form, {
        defaultValues: {
          reasonForRefund: 'td_refund_visa_refusal',
          refundDetails: ''
        },
        onSubmit: function onSubmit(values) {
          createSelfServeRequest({
            applicationId: application.id,
            requestReason: values.reasonForRefund,
            otherReasonDetail: values.refundDetails || undefined
          }, {
            onSuccess: function onSuccess() {
              props.onSuccess();
            },
            onError: function onError() {
              toasts.negative(new Error('Your request cannot be completed due to an error on our end. Please try again in a few minutes. If the error persists please contact our support team.'));
            }
          });
        },
        id: refundFormId,
        "aria-label": "Request Tuition Refund Form",
        children: _jsxs(Flex, {
          direction: "column",
          gap: 4,
          children: [_jsx(Notice, {
            children: "Please note that sending a tuition refund request does not guarantee that it will be approved."
          }), _jsx(Text, {
            variant: "bodyS",
            children: "You are about to request a tuition refund. Please provide a reason below."
          }), _jsxs(Field, {
            as: Select,
            label: "Reason for Refund",
            name: "reasonForRefund",
            appearance: "styled",
            children: [_jsx(Select.Option, {
              label: "Visa refusal",
              value: "td_refund_visa_refusal"
            }), _jsx(Select.Option, {
              label: "Visa pending/processing",
              value: "td_refund_visa_pending_or_processing"
            }), _jsx(Select.Option, {
              label: "Visa not yet submitted",
              value: "td_refund_visa_not_yet_submitted"
            }), _jsx(Select.Option, {
              label: "Interview delayed",
              value: "td_refund_visa_interview_delayed"
            }), _jsx(Select.Option, {
              label: "Financial difficulties",
              value: "td_refund_financial_difficulties"
            }), _jsx(Select.Option, {
              label: "Late visa approval",
              value: "td_refund_late_visa_approval"
            }), _jsx(Select.Option, {
              label: "Work commitment",
              value: "td_refund_work_commitment"
            }), _jsx(Select.Option, {
              label: "Family commitment",
              value: "td_refund_family_commitment"
            }), _jsx(Select.Option, {
              label: "Application waitlisted or program closed",
              value: "td_refund_application_waitlisted_or_program_closed"
            }), _jsx(Select.Option, {
              label: "IELTS pending",
              value: "td_refund_ielts_pending"
            }), _jsx(Select.Option, {
              label: "Personal reason",
              value: "td_refund_personal_reason"
            }), _jsx(Select.Option, {
              label: "Medical reason",
              value: "td_refund_medical_reason"
            }), _jsx(Select.Option, {
              label: "LOA revoked",
              value: "td_refund_loa_revoked"
            }), _jsx(Select.Option, {
              label: "Refused for PAL",
              value: "td_refund_refused_for_pal"
            }), _jsx(Select.Option, {
              label: "Other",
              value: "td_refund_other"
            })]
          }), _jsx(DeferralReasonField, {})]
        })
      })
    }), _jsx(Dialog.Actions, {
      children: createdSelfServeRequest ? _jsx(Button, {
        width: "fill",
        intent: "primary",
        onClick: function onClick() {
          resetCreateSelfServeRequest();
          props.onOpenChange(false);
        },
        children: "OK"
      }) : _jsxs(_Fragment, {
        children: [_jsx(Dialog.CloseButton, {
          width: "fill",
          children: "Cancel"
        }), _jsx(Button, {
          type: "submit",
          form: refundFormId,
          intent: "primary",
          width: "fill",
          loading: isCreatingSelfServeRequest,
          children: "Submit"
        })]
      })
    })]
  });
};
var DeferralReasonField = function DeferralReasonField() {
  var _useFieldValues = useFieldValues(['reasonForRefund']),
    reasonForRefund = _useFieldValues.reasonForRefund;
  if (reasonForRefund !== 'td_refund_other') {
    return null;
  }
  return _jsx(Field, {
    as: Textarea,
    helpText: "The maximum length of a text message is 250 characters.",
    label: "Please provide more details on reason for refund below",
    size: "lg",
    name: "refundDetails",
    required: "Please provide a reason for refund.",
    validate: function validate(refundDetails) {
      return refundDetails.length > 250 ? 'You have exceeded the maximum number of 250 characters in the field.' : true;
    }
  });
};